/**
 * Clock-in / Clock-out reminder banner (iter 169).
 *
 * Mounted once globally in Layout.jsx so every signed-in staff route
 * gets the nudge. Shows two reminders driven off SAST wall-clock:
 *
 *   • 07:00 → 09:00 SAST  — "You haven't clocked in yet" (if status="out")
 *   • 16:30 → 18:00 SAST  — "Don't forget to clock out" (if status="in")
 *
 * Behaviour:
 *   - Probes /api/team/attendance/me every 60s while in the active
 *     window. Banner appears inline at the top of the page (NO modal,
 *     per Trust Contract).
 *   - "Open Clock" → routes to /clock?mode=personal.
 *   - "Remind me in…" → 10 / 30 / 60 min snooze, or custom time picker.
 *     Snoozes persist in localStorage and survive reload.
 *   - Suppressed for owners/admins by default (they often aren't on
 *     the floor); enabled for technician, rep, manager, support-officer.
 *     Admin/owner can opt in via localStorage key `hv_clock_reminder_on`.
 *   - Suppressed on demo tenant + for clients.
 *   - Suppressed on weekends (Sat/Sun) — most field crews don't work.
 *   - Suppressed on the /clock page itself (would be silly).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Clock, LogIn, LogOut, X, AlertCircle, ChevronDown } from "lucide-react";
import api from "../../lib/api";
import { SAST_TZ } from "../../lib/datetime";
import { useAuth } from "../../context/AuthContext";

const LS_SNOOZE_IN = "hv_clock_snooze_in_until";   // ms epoch
const LS_SNOOZE_OUT = "hv_clock_snooze_out_until"; // ms epoch
const LS_DISMISSED_DAY_IN = "hv_clock_dismissed_in_date";   // YYYY-MM-DD SAST
const LS_DISMISSED_DAY_OUT = "hv_clock_dismissed_out_date"; // YYYY-MM-DD SAST
const LS_OPT_IN = "hv_clock_reminder_on"; // "1" to force-enable for owner/admin

// Reminder windows (SAST wall-clock).
const WIN_IN_FROM = 7 * 60;   // 07:00
const WIN_IN_TO   = 9 * 60;   // 09:00
const WIN_OUT_FROM = 16 * 60 + 30; // 16:30
const WIN_OUT_TO   = 18 * 60;       // 18:00
// Iter 181 (2026-02-21, Dirk) — goal-bound persistence: after the
// regular window closes, the OS notification repeats every REPEAT_MIN
// minutes (default 120) until the user clocks out OR the next morning
// rollover kicks in. The banner stays mounted the whole time.
const REPEAT_MIN = 120;
const LS_LAST_REPEAT_OUT = "hv_clock_last_repeat_out"; // ms epoch
const LS_LAST_REPEAT_IN  = "hv_clock_last_repeat_in";

// 2026-02-20 (iter 172, Dirk) — owner + admin INCLUDED by default.
// On HidroVerse / small-team accounts the owner is often on the floor
// and needs the same clock-in/out nudges as a tech. Larger tenants can
// opt OUT individually via localStorage.hv_clock_reminder_off=1.
const ROLES_DEFAULT = new Set([
  "owner", "admin", "manager", "technician", "rep", "support-officer",
]);

// ── SAST helpers ────────────────────────────────────────────────
function _sastParts(d = new Date()) {
  // Uses Intl with timeZone=SAST to derive wall-clock parts. Robust to
  // host TZ (laptop set to UTC, phone set to local, etc.).
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: SAST_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", weekday: "short",
    hour12: false,
  }).formatToParts(d);
  const obj = {};
  for (const p of parts) obj[p.type] = p.value;
  return {
    minutesOfDay: Number(obj.hour || 0) * 60 + Number(obj.minute || 0),
    dateKey: `${obj.year}-${obj.month}-${obj.day}`,
    dow: obj.weekday, // e.g. "Mon"
  };
}

const SNOOZE_OPTIONS = [
  { label: "10 min", min: 10 },
  { label: "30 min", min: 30 },
  { label: "1 hour", min: 60 },
];

// ── Component ───────────────────────────────────────────────────
export default function ClockReminderBanner() {
  const { user } = useAuth();
  const loc = useLocation();

  const [status, setStatus] = useState(null); // "in" | "out" | "break_start" | "break_end" | null
  const [lastEventAt, setLastEventAt] = useState(null); // ISO of latest event
  const [tick, setTick] = useState(0);
  const [snoozeOpen, setSnoozeOpen] = useState(null); // "in" | "out" | null
  const [customMin, setCustomMin] = useState("");

  // ── eligibility ─────────────────────────────────────────────
  const eligible = useMemo(() => {
    if (!user) return false;
    if (user.role === "client") return false;
    if (user.tenant_id === "demo") return false;
    if (loc.pathname.startsWith("/clock")) return false;
    if (ROLES_DEFAULT.has(user.role)) return true;
    return localStorage.getItem(LS_OPT_IN) === "1";
  }, [user, loc.pathname]);

  // ── status fetch (every 60s while banner is mounted) ───────
  useEffect(() => {
    if (!eligible) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get("/team/attendance/me");
        if (!alive) return;
        setStatus(r.data?.status || "out");
        setLastEventAt(r.data?.last_event?.at || null);
      } catch {
        if (!alive) return;
        setStatus(null);
        setLastEventAt(null);
      }
    };
    load();
    const id = setInterval(load, 60_000);
    // Iter 398i — Dirk reported the "you didn't clock out yesterday"
    // banner persisting even after a clock event was recorded. Root
    // cause: the banner only re-fetched status every 60s, so a user
    // who clocked in/out on another tab (or came back to this tab
    // after resolving the situation elsewhere) would keep staring at
    // stale state for up to a minute. Re-fetch on window focus and
    // on tab-visibility change so the banner clears the instant the
    // underlying event lands.
    const refetchIfVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", refetchIfVisible);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener("focus", load);
      document.removeEventListener("visibilitychange", refetchIfVisible);
    };
  }, [eligible]);

  // ── 30-second wall-clock tick so the window check re-evaluates. ───
  useEffect(() => {
    if (!eligible) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [eligible]);

  // ── true "pop-up" via browser/PWA notification (iter 172 / iter 181 Dirk) ───
  // Inline banners can be scrolled past; an OS notification can't be.
  // Iter 181 — goal-bound: clock-out notification REPEATS every
  // REPEAT_MIN minutes (default 120) as long as the user is still
  // clocked in. Snooze still suppresses it. Clock-in notification
  // remains fire-once-per-day inside the in-window.
  useEffect(() => {
    if (!eligible || status == null) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const { minutesOfDay, dateKey, dow } = _sastParts();
    if ((dow === "Sat" || dow === "Sun") && localStorage.getItem("hv_clock_reminder_weekend") !== "1") return;
    const inIn  = minutesOfDay >= WIN_IN_FROM  && minutesOfDay <= WIN_IN_TO  && status === "out";
    const stillClockedIn = (status === "in" || status === "break_start" || status === "break_end");
    // Clock-OUT notification now fires from WIN_OUT_FROM ONWARDS while
    // the user is still clocked in. No upper-bound window.
    const inOut = minutesOfDay >= WIN_OUT_FROM && stillClockedIn;
    if (!inIn && !inOut) return;
    const kind = inIn ? "in" : "out";
    // Skip if a snooze is active for this kind.
    const snoozeUntil = Number(localStorage.getItem(kind === "in" ? LS_SNOOZE_IN : LS_SNOOZE_OUT) || 0);
    if (snoozeUntil > Date.now()) return;
    // Skip if the user has dismissed for the day.
    const dismissed = localStorage.getItem(kind === "in" ? LS_DISMISSED_DAY_IN : LS_DISMISSED_DAY_OUT);
    if (dismissed === dateKey) return;
    // Throttle: clock-in fires once per day; clock-out repeats every
    // REPEAT_MIN minutes until done.
    const lastKey = kind === "in" ? LS_LAST_REPEAT_IN : LS_LAST_REPEAT_OUT;
    const last = Number(localStorage.getItem(lastKey) || 0);
    const sinceLast = Date.now() - last;
    if (kind === "in") {
      // Once-per-day for clock-in (existing behaviour).
      const firedKey = `hv_clock_notif_in_${dateKey}`;
      if (localStorage.getItem(firedKey)) return;
      localStorage.setItem(firedKey, String(Date.now()));
    } else {
      // Goal-bound repeat for clock-out.
      if (last && sinceLast < REPEAT_MIN * 60_000) return;
    }
    try {
      const note = new Notification(
        kind === "in" ? "Time to clock in" : "Don't forget to clock out",
        {
          body: kind === "in"
            ? "Your shift starts at 07:00 SAST. Tap to clock in now."
            : "You're still clocked in. Tap to clock out, or file an amendment if you forgot.",
          icon: "/icons/icon-192.png",
          badge: "/icons/icon-192.png",
          tag: `clock-${kind}-${dateKey}`,
          requireInteraction: true,
        },
      );
      note.onclick = () => {
        window.focus();
        window.location.href = "/clock?mode=personal";
      };
      localStorage.setItem(lastKey, String(Date.now()));
    } catch {
      /* notification API can throw on old Safari — fall back to inline banner */
    }
  }, [eligible, status, tick]);

  if (!eligible) return null;
  // Status not loaded yet — render nothing rather than guess.
  if (status == null) return null;

  const { minutesOfDay, dateKey, dow } = _sastParts();
  // Skip weekends — opt-in via localStorage `hv_clock_reminder_weekend=1`.
  // Dev/preview override (?hv_force_clock=…) skips the weekend gate too.
  const forceKind = (typeof window !== "undefined")
    ? new URLSearchParams(window.location.search).get("hv_force_clock")
    : null;
  if ((dow === "Sat" || dow === "Sun")
      && localStorage.getItem("hv_clock_reminder_weekend") !== "1"
      && forceKind !== "in" && forceKind !== "out") {
    return null;
  }

  const inWindowIn  = forceKind === "in"
    || (minutesOfDay >= WIN_IN_FROM  && minutesOfDay <= WIN_IN_TO  && status === "out");
  // Iter 181 — clock-OUT is goal-bound: once you're clocked in (status
  // === "in" or "break_*"), the banner shows from WIN_OUT_FROM onwards
  // until you actually clock out. It NO LONGER hides at 18:00. If
  // midnight passes with no clock-out, the banner morphs to "You didn't
  // clock out yesterday — file an amendment" (handled by the kind
  // label below).
  const stillClockedIn = (status === "in" || status === "break_start" || status === "break_end");
  // Detect "stale" clock-in carrying over from a previous SAST day.
  // If the last event is in a SAST date earlier than today AND we're
  // still in a non-"out" state, surface the banner ALL DAY so the user
  // can either clock-out now or file a backdated claim.
  let staleFromYesterday = false;
  if (stillClockedIn && lastEventAt) {
    try {
      const evParts = new Intl.DateTimeFormat("en-CA", {
        timeZone: SAST_TZ, year: "numeric", month: "2-digit", day: "2-digit",
      }).formatToParts(new Date(lastEventAt));
      const evObj = {};
      for (const p of evParts) evObj[p.type] = p.value;
      const evDate = `${evObj.year}-${evObj.month}-${evObj.day}`;
      if (evDate < dateKey) staleFromYesterday = true;
    } catch { /* parse failure → ignore */ }
  }
  const inWindowOut = forceKind === "out"
    || (minutesOfDay >= WIN_OUT_FROM && stillClockedIn)
    || staleFromYesterday;

  // No reminder needed.
  if (!inWindowIn && !inWindowOut) return null;

  // Honour the per-day dismissal flag — EXCEPT when the banner is
  // stale-from-yesterday (an outstanding clock-out from a prior day);
  // dismissing those silently is exactly what caused Dirk's 12:59
  // surprise. They MUST stay visible until resolved.
  const todayKey = dateKey;
  if (inWindowIn && localStorage.getItem(LS_DISMISSED_DAY_IN) === todayKey) return null;
  if (inWindowOut && !staleFromYesterday
      && localStorage.getItem(LS_DISMISSED_DAY_OUT) === todayKey) return null;

  // Honour the snooze timestamp — also bypassed for stale-from-yesterday.
  const now = Date.now();
  if (inWindowIn) {
    const until = Number(localStorage.getItem(LS_SNOOZE_IN) || 0);
    if (until > now) return null;
  }
  if (inWindowOut && !staleFromYesterday) {
    const until = Number(localStorage.getItem(LS_SNOOZE_OUT) || 0);
    if (until > now) return null;
  }

  const kind = inWindowIn ? "in" : "out";
  const isIn = kind === "in";

  const snooze = (minutes) => {
    const until = Date.now() + minutes * 60_000;
    localStorage.setItem(isIn ? LS_SNOOZE_IN : LS_SNOOZE_OUT, String(until));
    setSnoozeOpen(null);
    setCustomMin("");
    setTick((t) => t + 1);
  };
  const dismissForDay = () => {
    localStorage.setItem(isIn ? LS_DISMISSED_DAY_IN : LS_DISMISSED_DAY_OUT, todayKey);
    setTick((t) => t + 1);
  };
  const onCustomSnooze = (e) => {
    e.preventDefault();
    const m = Math.max(1, Math.min(720, Number(customMin || 0)));
    if (!m) return;
    snooze(m);
  };

  return (
    <div
      /* Iter 398i — Dirk reported "yellow on yellow, can't read" on
         a manager's light-mode / OS-light-theme view. The old palette
         used a deep-brown bg with pale-yellow text — great on a dark
         app shell, but readable-broke when the PWA rendered the shell
         in a lighter tone. Fix: force a saturated amber bg (900-shade)
         with a solid #fff foreground so contrast holds regardless of
         the surrounding shell colours. Same treatment for the cyan
         "time to clock in" variant. */
      className={`relative rounded-2xl border px-4 py-3 mb-4 shadow-md flex flex-wrap items-center gap-3 ${
        isIn
          ? "border-[#1FB6C1]/60 bg-[#083344] text-white"
          : "border-[#F59E0B]/70 bg-[#7c2d12] text-white"
      }`}
      data-testid={`clock-reminder-${kind}`}
    >
      <span
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
          isIn ? "bg-[#1FB6C1]/25 text-[#a5f3fc]" : "bg-[#F59E0B]/30 text-[#fde68a]"
        }`}
        aria-hidden="true"
      >
        {isIn ? <LogIn className="w-4 h-4" /> : <LogOut className="w-4 h-4" />}
      </span>
      <div className="flex-1 min-w-[180px]">
        <div className="font-semibold text-sm text-white">
          {isIn
            ? "Time to clock in"
            : staleFromYesterday
              ? "You didn't clock out yesterday"
              : "Don't forget to clock out"}
        </div>
        <div className="text-xs text-white/90">
          {isIn
            ? "Your shift starts at 07:00 SAST. Tap to clock in now."
            : staleFromYesterday
              ? "Your last clock-in is from a previous day. Clock out now, or file an amendment with the correct time."
              : "You're still clocked in. Tap to clock out, or set a reminder to come back."}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Link
          to="/clock?mode=personal"
          className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1"
          data-testid={`clock-reminder-${kind}-open`}
        >
          <Clock className="w-3.5 h-3.5" />
          {isIn ? "Clock in" : "Clock out"}
        </Link>

        {!isIn && (
          <Link
            to="/clock?mode=personal&claim=1"
            className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
            data-testid={`clock-reminder-${kind}-claim`}
          >
            File amendment
          </Link>
        )}

        <div className="relative">
          <button
            type="button"
            className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
            onClick={() => setSnoozeOpen((p) => (p === kind ? null : kind))}
            data-testid={`clock-reminder-${kind}-snooze-toggle`}
          >
            Remind me later <ChevronDown className="w-3 h-3" />
          </button>
          {snoozeOpen === kind && (
            <div
              className="absolute z-30 right-0 mt-1 min-w-[180px] rounded-xl border border-[#22344a] bg-[#0d1622] shadow-lg p-2"
              data-testid={`clock-reminder-${kind}-snooze-menu`}
            >
              {SNOOZE_OPTIONS.map((o) => (
                <button
                  key={o.min}
                  type="button"
                  className="block w-full text-left px-2 py-1 text-xs hover:bg-[#162232] rounded"
                  onClick={() => snooze(o.min)}
                  data-testid={`clock-reminder-${kind}-snooze-${o.min}`}
                >
                  In {o.label}
                </button>
              ))}
              <form
                onSubmit={onCustomSnooze}
                className="flex items-center gap-1 mt-1 pt-1 border-t border-[#22344a]"
              >
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={720}
                  value={customMin}
                  onChange={(e) => setCustomMin(e.target.value)}
                  placeholder="min"
                  className="hv-input text-xs w-20"
                  data-testid={`clock-reminder-${kind}-snooze-custom-input`}
                />
                <button
                  type="submit"
                  className="hv-btn hv-btn-ghost text-xs"
                  data-testid={`clock-reminder-${kind}-snooze-custom-apply`}
                >
                  Snooze
                </button>
              </form>
              <button
                type="button"
                className="block w-full text-left px-2 py-1 text-[10px] text-[#9aa3b5] hover:bg-[#162232] rounded mt-1 inline-flex items-center gap-1"
                onClick={dismissForDay}
                data-testid={`clock-reminder-${kind}-dismiss-today`}
              >
                <AlertCircle className="w-3 h-3" /> Don't show again today
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          aria-label="Dismiss"
          className="text-[#9aa3b5] hover:text-white"
          onClick={dismissForDay}
          data-testid={`clock-reminder-${kind}-close`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
